import { useCallback, useEffect, useState } from "react";
import { Badge, Col, Input, Row, Table } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { get, post } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context/auth";

const STATUS_COLORS = {
  draft: 'secondary',
  in_transit: 'warning',
  received: 'success',
  cancelled: 'danger',
};

function ViewTransferPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(false);
  // Receiving less than was dispatched is allowed; the difference stays in the
  // in-transit warehouse where it has to be accounted for.
  const [receivedQuantities, setReceivedQuantities] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const data = await get(`/inventory/stock-transfers/view/${id}`);
    if (data?.success) {
      setTransfer(data.transfer);
      setReceivedQuantities(
        Object.fromEntries((data.transfer.lines ?? []).map((l) => [l.id, l.quantity_dispatched]))
      );
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (action, payload = {}) => {
    setLoading(true);
    const data = await post(`/inventory/stock-transfers/${action}/${id}`, payload);
    if (data?.success) {
      toast.success(t(`transfers.${action}Success`));
      load();
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setLoading(false);
  };

  const receive = () => act('receive', {
    lines: (transfer.lines ?? []).map((line) => ({
      id: line.id,
      quantity_received: Number(receivedQuantities[line.id] ?? 0),
    })),
  });

  if (!transfer) {
    return (
      <SidebarLayout sidebar={sidebar}>
        <div className="page-with-subheader">
          <PageSubHeader title={t('transfers.title')} onBack={() => navigate('/inventory/stock-transfers')} />
        </div>
      </SidebarLayout>
    );
  }

  const isDraft = transfer.status === 'draft';
  const isInTransit = transfer.status === 'in_transit';

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('transfers.title'), transfer.transfer_number]}
          onBack={() => navigate('/inventory/stock-transfers')}
        >
          {isDraft && can('inventory.transfers.dispatch') && (
            <DefaultButton color="primary" size="sm" loading={loading} onClick={() => act('dispatch')}>
              {t('transfers.dispatch')}
            </DefaultButton>
          )}
          {isInTransit && can('inventory.transfers.receive') && (
            <DefaultButton color="success" size="sm" loading={loading} onClick={receive}>
              {t('transfers.receive')}
            </DefaultButton>
          )}
          {(isDraft || isInTransit) && can('inventory.transfers.edit') && (
            <DefaultButton color="warning" size="sm" outline loading={loading} onClick={() => act('cancel')}>
              {t('transfers.cancel')}
            </DefaultButton>
          )}
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-4">
            <Col md="3">
              <small className="text-muted d-block">{t('transfers.number')}</small>
              <strong>{transfer.transfer_number}</strong>
            </Col>
            <Col md="3">
              <small className="text-muted d-block">{t('common.status')}</small>
              <Badge color={STATUS_COLORS[transfer.status] ?? 'secondary'}>
                {t(`transfers.status.${transfer.status}`)}
              </Badge>
            </Col>
            <Col md="3">
              <small className="text-muted d-block">{t('transfers.from')}</small>
              <strong>{transfer.source_warehouse?.name ?? '-'}</strong>
            </Col>
            <Col md="3">
              <small className="text-muted d-block">{t('transfers.to')}</small>
              <strong>{transfer.destination_warehouse?.name ?? '-'}</strong>
            </Col>
          </Row>

          <Table bordered responsive size="sm">
            <thead>
              <tr>
                <th>{t('inventory.product')}</th>
                <th className="text-end">{t('transfers.requested')}</th>
                <th className="text-end">{t('transfers.dispatched')}</th>
                <th className="text-end">{t('transfers.received')}</th>
              </tr>
            </thead>
            <tbody>
              {(transfer.lines ?? []).map((line) => (
                <tr key={line.id}>
                  <td>{line.product?.name ?? line.product_name}</td>
                  <td className="text-end">{Number(line.quantity_requested)}</td>
                  <td className="text-end">{Number(line.quantity_dispatched)}</td>
                  <td className="text-end" style={{ maxWidth: 140 }}>
                    {isInTransit ? (
                      <Input
                        type="number"
                        bsSize="sm"
                        min="0"
                        max={line.quantity_dispatched}
                        step="0.001"
                        value={receivedQuantities[line.id] ?? ''}
                        onChange={(e) => setReceivedQuantities((prev) => ({ ...prev, [line.id]: e.target.value }))}
                      />
                    ) : (
                      Number(line.quantity_received)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {isInTransit && (
            <p className="text-muted small">{t('transfers.partialHint')}</p>
          )}

          {transfer.notes && (
            <>
              <small className="text-muted d-block mt-3">{t('common.notes')}</small>
              <p>{transfer.notes}</p>
            </>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}

export default ViewTransferPage;
